import { useState } from "react";
import { Link } from "react-router-dom";
import { getLeads, getBookings } from "@/lib/api";
import { Monogram } from "@/components/Monogram";

const fmtAED = (n) => `AED ${Number(n).toLocaleString("en-US")}`;

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("alvirah_admin_key") || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");

  const load = async (k) => {
    try {
      const [l, b] = await Promise.all([getLeads(k), getBookings(k)]);
      setLeads(l.data);
      setBookings(b.data);
      setError("");
    } catch {
      setError("Incorrect passcode. Please try again.");
      sessionStorage.removeItem("alvirah_admin_key");
      setKey("");
    }
  };

  const submit = (e) => {
    e.preventDefault();
    sessionStorage.setItem("alvirah_admin_key", input);
    setKey(input);
    load(input);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-cream text-plum">
      <header className="border-b hairline px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="admin-home-link" className="flex items-center gap-3">
          <Monogram className="h-7 w-auto" />
          <span className="font-serif text-sm tracking-[0.18em] uppercase">House of Alvirah — Atelier Admin</span>
        </Link>
      </header>

      {!key ? (
        <div className="max-w-md mx-auto mt-32 px-6">
          <p className="overline-label mb-4">Restricted</p>
          <h1 className="font-serif text-3xl mb-8">Enter the atelier passcode</h1>
          <form onSubmit={submit} data-testid="admin-login-form" className="flex flex-col gap-4">
            <input
              data-testid="admin-passcode-input"
              type="password"
              placeholder="Passcode"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="w-full bg-transparent border border-plum/25 focus:border-gold-dark outline-none px-5 py-4 text-sm font-light tracking-wide placeholder:text-plum/35"
            />
            {error && <p data-testid="admin-login-error" className="text-xs text-wine">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="bg-gold text-cream text-[11px] uppercase tracking-[0.25em] px-8 py-4 hover:bg-plum hover:text-cream transition-colors duration-300"
            >
              Enter
            </button>
          </form>
        </div>
      ) : (
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
          <div className="flex items-center gap-8 border-b hairline mb-10">
            {["leads", "bookings"].map((t) => (
              <button
                key={t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`pb-4 text-[11px] uppercase tracking-[0.3em] transition-colors duration-300 ${
                  tab === t ? "text-plum border-b border-gold-dark -mb-px" : "text-plum/40 hover:text-plum"
                }`}
              >
                {t === "leads" ? `Quiz Leads (${leads?.length ?? 0})` : `Discovery Calls (${bookings?.length ?? 0})`}
              </button>
            ))}
          </div>

          {tab === "leads" && (
            <div data-testid="admin-leads-table" className="overflow-x-auto">
              <table className="w-full text-sm font-light">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-plum/50 border-b hairline">
                    <th className="py-4 pr-6 font-medium">Name</th>
                    <th className="py-4 pr-6 font-medium">Contact</th>
                    <th className="py-4 pr-6 font-medium">Property</th>
                    <th className="py-4 pr-6 font-medium">Area</th>
                    <th className="py-4 pr-6 font-medium">Size</th>
                    <th className="py-4 pr-6 font-medium">Style</th>
                    <th className="py-4 pr-6 font-medium">Estimate</th>
                  </tr>
                </thead>
                <tbody>
                  {(leads || []).map((l) => (
                    <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b hairline hover:bg-gold/10 transition-colors duration-200">
                      <td className="py-4 pr-6 font-serif text-base">{l.name}</td>
                      <td className="py-4 pr-6">
                        <div>{l.email}</div>
                        <div className="text-plum/50 text-xs">{l.phone}</div>
                      </td>
                      <td className="py-4 pr-6">{l.property_type}</td>
                      <td className="py-4 pr-6">{l.area}</td>
                      <td className="py-4 pr-6">{l.sqft}</td>
                      <td className="py-4 pr-6">{l.style}</td>
                      <td className="py-4 pr-6 whitespace-nowrap">{fmtAED(l.estimate_low)} – {fmtAED(l.estimate_high)}</td>
                    </tr>
                  ))}
                  {leads && leads.length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-plum/40">No leads yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "bookings" && (
            <div data-testid="admin-bookings-table" className="overflow-x-auto">
              <table className="w-full text-sm font-light">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-plum/50 border-b hairline">
                    <th className="py-4 pr-6 font-medium">Name</th>
                    <th className="py-4 pr-6 font-medium">Contact</th>
                    <th className="py-4 pr-6 font-medium">Date</th>
                    <th className="py-4 pr-6 font-medium">Time (GST)</th>
                  </tr>
                </thead>
                <tbody>
                  {(bookings || []).map((b) => (
                    <tr key={b.id} data-testid={`admin-booking-row-${b.id}`} className="border-b hairline hover:bg-gold/10 transition-colors duration-200">
                      <td className="py-4 pr-6 font-serif text-base">{b.name}</td>
                      <td className="py-4 pr-6">
                        <div>{b.email}</div>
                        <div className="text-plum/50 text-xs">{b.phone}</div>
                      </td>
                      <td className="py-4 pr-6">{b.date}</td>
                      <td className="py-4 pr-6">{b.time}</td>
                    </tr>
                  ))}
                  {bookings && bookings.length === 0 && (
                    <tr><td colSpan={4} className="py-12 text-center text-plum/40">No bookings yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
